import i18next from 'i18next';
import common from '../../common';
import Text from '../../ui/Text';
import Scene from '../Scene';
import ShareManager from '../../utils/ShareManager';
import WarningDialog from '../../ui/WarningDialog';

export default class SettingsScene extends Scene {
	constructor(source) {
		super();

		this._source = source;
		this._settingsPage = null;
		this._languagePage = null;
		this._fade = null;
		this._panel = null;
		this._langEntries = [];
		this._langConfirmButton = null;
		this._langCancelButton = null;
		this._langScrollView = null;
		this._keyUpSignal = null;
		this._warningDialog = null;

		this._scrollUpHeld = false;
		this._scrollDownHeld = false;

		this._selectedLanguage = common.config.language;
		this._currentLanguage = common.config.language;

		this.signals.restart = new P3.utils.MiniSignal();
	}

	init() {
		super.init();
		this._keyUpSignal = P3.input.signals.keyUp.add(this._onKeyUp, this);

		this._fade = new PIXI.Graphics();
		this._fade.beginFill(0x000000);
		this._fade.drawRect(0, 0, P3.View.width, P3.View.height);
		this._fade.endFill();
		this._fade.interactive = true;
		this._fade.alpha = 0;
		this.addChild(this._fade);

		this._panel = new PIXI.NineSlicePlane(P3.assets.texture('panel_popup_9slice'), 50, 90, 50, 50);
		this._panel.width = 600;
		this._panel.height = 725;
		this.addChild(this._panel);

		const states = new P3.ButtonStates();

		states.normal = P3.assets.texture('btn_close_out');
		states.over = P3.assets.texture('btn_close_over');

		const closeButton = common.buttonFactory.createButton(states);

		closeButton.animate = false;
		closeButton.position.set(this._panel.width - 55, 60);
		closeButton.signals.click.add(this._onNextClick, this);
		this._panel.addChild(closeButton);

		closeButton.accessibleTitle = i18next.t('accessibility.button.close');

		const titleLabel = new Text('gui.settings.paused', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });

		titleLabel.position.set(50, 45);
		this._panel.addChild(titleLabel);

		this._settingsPage = this._createSettingsPage();
		this._panel.addChild(this._settingsPage);

		this._languagePage = this._createLanguagePage();
		this._languagePage.visible = false;
		this._panel.addChild(this._languagePage);
	}

	_createSettingsPage() {
		const panel = new PIXI.Container();

		const langButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_black_outline_9slice_out', width: 438, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_black_outline_9slice_over', width: 438, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});

		langButton.animate = false;
		langButton.position.set(this._panel.width * 0.5, 166);
		langButton.signals.click.add(() => {
			this._languagePage.visible = true;
			this._settingsPage.visible = false;
		});
		panel.addChild(langButton);

		const t = i18next.t('gui.settings.language', { CURRENTLANG: common.i18nConfig.languages[common.config.language] });
		const langLabel = new Text(t, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });

		langLabel.signals.languageChange.add(() => {
			langLabel.text = i18next.t('gui.settings.language', { CURRENTLANG: common.i18nConfig.languages[common.config.language] });
		});
		langLabel.position.set(langButton.width * -0.5 + 30, 0);
		langLabel.anchor.set(0, 0.5);
		langButton.addChild(langLabel);

		langButton.accessibleTitle = langLabel.text;

		const soundButton = this._createSettingsItem('gui.settings.sound', common.save.game.sound);

		soundButton.position.set(82, 250);
		soundButton.button.signals.click.add(() => this._onSettingToggle('sound'));
		panel.addChild(soundButton);

		const motionButton = this._createSettingsItem('gui.settings.motion', common.save.game.motion);

		motionButton.position.set(82, 330);
		motionButton.button.signals.click.add(() => this._onSettingToggle('motion'));
		panel.addChild(motionButton);

		let divider = new PIXI.Sprite(P3.assets.texture('divider'));

		divider.anchor.set(0.5);
		divider.width = 438;
		divider.height = 2;
		divider.position.set(this._panel.width * 0.5, 410);
		panel.addChild(divider);

		const sharing = this._createSettingsSharing();

		sharing.position.set(0, 480);
		panel.addChild(sharing);

		divider = new PIXI.Sprite(P3.assets.texture('divider'));
		divider.anchor.set(0.5);
		divider.width = 438;
		divider.height = 2;
		divider.position.set(this._panel.width * 0.5, 550);
		panel.addChild(divider);

		if (this._source === 'game') {
			const homeButton = common.buttonFactory.createButton({
				normal: { textureName: 'btn_black_outline_9slice_out', width: 74, height: 74, left: 35, top: 35, right: 35, bottom: 35 },
				over: { textureName: 'btn_black_outline_9slice_over', width: 74, height: 74, left: 35, top: 35, right: 35, bottom: 35 },
				icon: P3.assets.texture('icon_home'),
			});

			homeButton.position.set(120, 625);
			homeButton.signals.click.add(this._onHomeClick, this);
			panel.addChild(homeButton);

			homeButton.accessibleTitle = i18next.t('accessibility.button.home');

			const restartButton = common.buttonFactory.createButton({
				normal: { textureName: 'btn_black_outline_9slice_out', width: 74, height: 74, left: 35, top: 35, right: 35, bottom: 35 },
				over: { textureName: 'btn_black_outline_9slice_over', width: 74, height: 74, left: 35, top: 35, right: 35, bottom: 35 },
				icon: P3.assets.texture('icon_restart'),
			});

			restartButton.position.set(this._panel.width - 120, 625);
			restartButton.signals.click.add(this._onRestartClick, this);
			panel.addChild(restartButton);

			restartButton.accessibleTitle = i18next.t('accessibility.button.restart');
		}

		const okButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 200, height: 74, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 200, height: 74, left: 35, top: 35, right: 35, bottom: 35 },
		});

		okButton.position.set(this._panel.width * 0.5, 625);
		okButton.signals.click.add(this._onNextClick, this);
		panel.addChild(okButton);

		const okLabel = new Text('gui.general.options.ok', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });

		okLabel.anchor.set(0.5);
		okButton.animation.addChild(okLabel);

		okButton.accessibleTile = okLabel.text;

		return panel;
	}

	_createSettingsSharing() {
		const panel = new PIXI.Container();

		if (ShareManager.ShareSupported()) {
			// create a single share button

			const shareButton = common.buttonFactory.createButton({
				normal: { textureName: 'btn_black_outline_9slice_out', width: 200, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
				over: { textureName: 'btn_black_outline_9slice_over', width: 200, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			});

			shareButton.animate = true;
			shareButton.position.set(this._panel.width * 0.5, 0);
			shareButton.signals.click.add(() => {
				common.tracking.track(new P3.TrackingDataGtagEvent('social_share', {
					event_category: 'UI',
					event_type: 'share_native',
					page_container: this._source || 'settings',
				}));

				ShareManager.share();
			});
			panel.addChild(shareButton);

			const label = new Text('gui.splashscreen.buttons.share', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });

			label.anchor.set(0.5);
			shareButton.animation.addChild(label);

			shareButton.accessibleTitle = label.text;
		} else {
			// create social media buttons

			const logos = ['facebook', 'x', 'linkedin', 'email'];
			const buttonContainer = new PIXI.Container();

			for (let i = 0; i < logos.length; i++) {
				const btn = common.buttonFactory.createButton(new P3.ButtonStates(P3.assets.texture(`logo_${logos[i]}`)));
				// btn.anchor.set(0, 0.5);

				btn.position.set(100 * i, 0);
				btn.signals.click.add(() => this._onShareClick(logos[i]));
				buttonContainer.addChild(btn);

				btn.accessibleTitle = i18next.t(`accessibility.button.share_${logos[i]}`);
			}

			buttonContainer.x = this._panel.width * 0.5 - buttonContainer.width * 0.5 + buttonContainer.children[0].width * 0.5;
			panel.addChild(buttonContainer);
		}

		return panel;
	}

	_createSettingsItem(text, state) {
		const panel = new PIXI.Container();

		const label = new Text(text, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });

		label.anchor.set(0, 0.5);
		label.position.set(30, 0);
		panel.addChild(label);

		const states = new P3.ButtonStates();

		states.normal = P3.assets.texture('btn_switch_on_out');
		states.over = P3.assets.texture('btn_switch_on_over');
		states.normalAlt = P3.assets.texture('btn_switch_off_out');
		states.overAlt = P3.assets.texture('btn_switch_off_over');

		const button = new P3.ToggleButton(states);

		button.overSoundName = 'sfx_genericrollover';
		button.clickSoundName = 'sfx_genericclick';
		button.animate = false;
		button.position.set(374, 0);
		button.toggle = !state;
		panel.addChild(button);

		button.accessible = true;
		button.accessibleTitle = label.text;

		panel.button = button;

		return panel;
	}

	_onSettingToggle(type) {
		switch (type) {
		case 'sound':
			P3.audio.mute(!P3.audio.isMuted());

			common.save.game.sound = !P3.audio.isMuted();
			common.save.saveGame();

			common.tracking.track(new P3.TrackingDataGtagEvent('setting_changed', {
				event_category: 'UI',
				event_type: `sound_${P3.audio.isMuted() ? 'off' : 'on'}`,
				page_container: 'settings',
			}));
			break;
		case 'motion':
			common.save.game.motion = common.motion = !common.save.game.motion;
			common.motionToggle.dispatch(common.motion);
			common.save.saveGame();

			common.tracking.track(new P3.TrackingDataGtagEvent('setting_changed', {
				event_category: 'UI',
				event_type: `motion_${common.save.game.motion ? 'on' : 'off'}`,
				page_container: 'settings',
			}));
			break;
		default:
			break;
		}
	}

	_onKeyUp(event) {
		if (P3.scene.top === this && event.key === 'Backspace') {
			this._onNextClick();
		}
	}

	_onShareClick(option) {
		common.tracking.track(new P3.TrackingDataGtagEvent('social_share', {
			event_category: 'UI',
			event_type: `share_${option}`,
			page_container: this._source || 'settings',
		}));

		switch (option) {
		case 'facebook':
			ShareManager.shareFacebook();
			break;
		case 'x':
			ShareManager.shareTwitter();
			break;
		case 'linkedin':
			ShareManager.shareLinkedin();
			break;
		case 'email':
			ShareManager.shareEmail();
			break;
		default:
			break;
		}
	}

	_createLanguagePage() {
		const panel = new PIXI.Container();

		const border = new PIXI.NineSlicePlane(P3.assets.texture('btn_black_outline_9slice_out'), 35, 35, 35, 35);

		border.width = 438;
		border.height = 417;
		border.position.set(this._panel.width * 0.5 - border.width * 0.5, this._panel.height * 0.5 - border.height * 0.5 - 25);
		panel.addChild(border);

		const scrollheight = border.height - 6;

		this._langScrollView = new P3.utils.ScrollView();
		this._langScrollView.contentBounds = new PIXI.Rectangle();
		this._langScrollView.hitArea = new PIXI.Rectangle(0, 0, border.width, scrollheight);
		this._langScrollView.position.set(border.x, border.y + 4);
		panel.addChild(this._langScrollView);

		const mask = new PIXI.NineSlicePlane(P3.assets.texture('mask_01'), 35, 35, 35, 35);

		mask.width = border.width;
		mask.height = scrollheight;

		this._langScrollView.content.mask = new PIXI.Sprite(common.renderer.generateTexture(mask));
		this._langScrollView.content.mask.width = border.width;
		this._langScrollView.content.mask.height = scrollheight;
		this._langScrollView.addChild(this._langScrollView.content.mask);

		/** SCROLL BAR **/
		this.scrollBar = this._createScrollBar(scrollheight - 80);
		this.scrollBar.position.x = this._langScrollView.x + border.width + 15;
		this.scrollBar.position.y = this._langScrollView.y + 40;
		panel.addChild(this.scrollBar);

		const height = 70;

		let i = 0;

		for (const lang in common.i18nConfig.languages) {
			const name = common.i18nConfig.languages[lang];
			const isSelected = lang === common.config.language;
			const entry = new PIXI.Container();

			entry.y = height * i;
			entry.hitArea = new PIXI.Rectangle(0, 0, border.width, height);

			const selectFill = new PIXI.Graphics();

			selectFill.beginFill(0xd6ffd0);
			selectFill.drawRect(0, 0, border.width - 5, height);
			selectFill.endFill();
			selectFill.alpha = isSelected ? 0.75 : 0;
			entry.addChild(selectFill);

			const label = new Text(name, { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });

			label.anchor.set(0, 0.5);
			label.position.set(30, height * 0.5);
			entry.addChild(label);

			const selectedIcon = new PIXI.Container();

			entry.addChild(selectedIcon);

			const outline = new PIXI.Sprite(P3.assets.texture('btn_green_outline_small_out'));

			outline.anchor.set(0.5);
			selectedIcon.addChild(outline);

			const check = new PIXI.Sprite(P3.assets.texture('icon_tick'));

			check.anchor.set(0.5);
			selectedIcon.addChild(check);

			selectedIcon.position.set(border.width - 45, height * 0.5);
			selectedIcon.visible = isSelected;

			const divider = new PIXI.Sprite(P3.assets.texture('divider'));

			divider.width = border.width - 40;
			divider.height = 1;
			divider.alpha = 0.5;
			divider.position.set(20, height);
			entry.addChild(divider);

			entry.type = lang;
			entry.selectedIcon = selectedIcon;
			entry.selectFill = selectFill;
			entry.interactive = true;
			entry.buttonMode = true;

			entry.on('pointerover', this._entryOnPointerOver.bind(this, entry));
			entry.on('pointerout', this._entryOnPointerOut.bind(this, entry));
			entry.on('pointertap', this._entryOnPointerTap.bind(this, entry));

			this._langScrollView.content.addChild(entry);
			this._langEntries.push(entry);
			i++;
		}

		const bounds = this._langScrollView.contentBounds;

		bounds.y = -this._langEntries.length * height + bounds.height;
		if (bounds.height - bounds.y > 400) {
			bounds.y += 400;
		} else {
			bounds.y = bounds.height;
			this.scrollBar.visible = false;
		}

		this._langConfirmButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_blue_fill_large_9slice_out', width: 200, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_blue_fill_large_9slice_over', width: 200, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});
		this._langConfirmButton.position.set(this._panel.width * 0.5 + this._langConfirmButton.width * 0.5 + 10, this._panel.height - 100);
		this._langConfirmButton.signals.click.add(this._onLanguageConfirmClick, this);
		panel.addChild(this._langConfirmButton);

		this._langConfirmButton.label = new Text('gui.general.options.ok', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0xFFFFFF });
		this._langConfirmButton.label.anchor.set(0.5);
		this._langConfirmButton.animation.addChild(this._langConfirmButton.label);

		this._langConfirmButton.accessibleTitle = this._langConfirmButton.label.text;

		this._langCancelButton = common.buttonFactory.createButton({
			normal: { textureName: 'btn_black_outline_9slice_out', width: 200, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
			over: { textureName: 'btn_black_outline_9slice_out', width: 200, height: 75, left: 35, top: 35, right: 35, bottom: 35 },
		});
		this._langCancelButton.position.set(this._panel.width * 0.5 - this._langCancelButton.width * 0.5 - 10, this._panel.height - 100);
		this._langCancelButton.signals.click.add(this._onLanguageCancelClick, this);
		panel.addChild(this._langCancelButton);

		this._langCancelButton.label = new Text('gui.development.footer.cancel', { font: '32px', fontWeight: 'bold', align: 'left', tint: 0x0 });
		this._langCancelButton.label.anchor.set(0.5);
		this._langCancelButton.animation.addChild(this._langCancelButton.label);

		this._langCancelButton.accessibleTitle = this._langCancelButton.label.text;

		return panel;
	}

	_createScrollBar(height) {
		const scroll = new PIXI.Container();

		scroll.upButton = common.buttonFactory.createButton({
			normal: P3.assets.texture('btn_scroll_chevron_out'),
		});

		scroll.upButton.signals.down.add(this.onScrollbarUpButtonPointerDown, this);
		scroll.upButton.signals.click.add(this.onScrollbarUpButtonPointerUp, this);
		scroll.upButton.position.set(7, -20);

		scroll.upButton.accessibleTitle = i18next.t('accessibility.button.scrollUp');

		scroll.thumb = new PIXI.NineSlicePlane(P3.assets.texture('scroll_fg_9slice'), 12, 12, 12, 12);
		scroll.thumb.height = 50;
		scroll.thumb.width = 15;
		scroll.thumb.interactive = true;
		scroll.thumb.buttonMode = true;
		scroll.thumb.on('mousedown', this.onScrollBarThumbPointerDown, this);
		scroll.thumb.on('mouseup', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('mouseupoutside', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('touchstart', this.onScrollBarThumbPointerDown, this);
		scroll.thumb.on('touchend', this.onScrollBarThumbPointerUp, this);
		scroll.thumb.on('touchendoutside', this.onScrollBarThumbPointerUp, this);

		scroll.track = new PIXI.NineSlicePlane(P3.assets.texture('scroll_bg_9slice'), 12, 12, 12, 12);
		scroll.track.height = height;
		scroll.track.width = scroll.thumb.width;

		scroll.downButton = common.buttonFactory.createButton({
			normal: P3.assets.texture('btn_scroll_chevron_out'),
		});
		scroll.downButton.signals.down.add(this.onScrollbarDownButtonPointerDown, this);
		scroll.downButton.signals.up.add(this.onScrollbarDownButtonPointerUp, this);
		scroll.downButton.scale.y *= -1;
		scroll.downButton.position.set(7, height + 20);

		scroll.downButton.accessibleTitle = i18next.t('accessibility.button.scrollDown');

		scroll.addChild(scroll.upButton);
		scroll.addChild(scroll.track);
		scroll.addChild(scroll.thumb);
		scroll.addChild(scroll.downButton);

		return scroll;
	}

	onScrollBarThumbPointerDown(event) {
		this._isScrolling = true;
		this._scrollOffset = event.data.getLocalPosition(this.scrollBar.thumb);
	}

	onScrollBarThumbPointerUp() {
		this._isScrolling = false;
	}

	onScrollbarUpButtonPointerDown() {
		this._scrollUpHeld = true;
	}

	onScrollbarUpButtonPointerUp() {
		this._scrollUpHeld = false;
	}

	onScrollbarDownButtonPointerDown() {
		this._scrollDownHeld = true;
	}

	onScrollbarDownButtonPointerUp() {
		this._scrollDownHeld = false;
	}

	updateScrollThumb() {
		const thumb = this.scrollBar.thumb;
		const thumbRange = this.scrollBar.track.height - thumb.height + 5;

		if (this._isScrolling) {
			const position = this.scrollBar.toLocal(P3.input.touch, common.stage);

			thumb.y = Math.min(thumbRange, Math.max(0, position.y - this._scrollOffset.y));
			this._langScrollView.content.y = this._langScrollView.contentBounds.height + ((thumb.y / thumbRange)
				* (this._langScrollView.contentBounds.y - this._langScrollView.contentBounds.height));
		} else {
			const contentRange = this._langScrollView.contentBounds.y - this._langScrollView.contentBounds.height;
			const f = (this._langScrollView.content.y - this._langScrollView.contentBounds.height) / contentRange;

			thumb.y = Math.min(thumbRange, Math.max(0, f * thumbRange));
		}
	}

	_entryOnPointerOver(entry) {
		const isSelected = entry.type === common.config.language;

		entry.selectFill.alpha = isSelected ? 0.75 : 0.2;
	}

	_entryOnPointerOut(entry) {
		const isSelected = entry.type === common.config.language;

		entry.selectFill.alpha = isSelected ? 0.75 : 0;
	}

	_entryOnPointerTap(entry) {
		if (entry.type === this._selectedLanguage || this._langScrollView.isDragging) return;

		this._selectedLanguage = entry.type;

		entry.selectedIcon.visible = true;
		common.languageManager.onLanguageChanged(entry.type);

		common.languageManager.signals.loaded.once(this._updateConfirmButton, this);

		for (let i = 0; i < this._langEntries.length; i++) {
			this._langEntries[i].selectedIcon.visible = false;
			this._langEntries[i].selectFill.alpha = 0;
		}

		entry.selectedIcon.visible = true;
		entry.selectFill.alpha = 0.75;
	}

	_updateConfirmButton() {
		this._langConfirmButton.label.onLanguageChange();
		this._langCancelButton.label.onLanguageChange();
	}

	_onLanguageConfirmClick() {
		this._panel.visible = false;

		this._langCancelButton.enabled = false; // prevent bubbling
		this._langConfirmButton.enabled = false;

		this._warningDialog = new WarningDialog();
		this._warningDialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);

		this._warningDialog.body = i18next.t('gui.settings.languageConfirm');
		this.addChild(this._warningDialog);

		this._warningDialog.signals.confirm.add(() => {
			this._warningDialog = null;
			// As we've already prompted the user once, no need to nag again
			window.unLoadType = 'Language'; // hacky, but only way I can find to pass param to unload event
			location.reload();
		});

		this._warningDialog.signals.deny.add(() => {
			const entry = this._langEntries.find((e) => e.type === this._currentLanguage);

			this._entryOnPointerTap(entry);

			P3.utils.performNextTick(() => {	// prevent bubbling
				this._panel.visible = true;
				this._langConfirmButton.enabled = true;
				this._langCancelButton.enabled = true;
			});

			this._warningDialog = null;
		});
	}

	_onLanguageCancelClick() {
		if (this._currentLanguage !== common.config.language) {
			const entry = this._langEntries.find((e) => e.type === this._currentLanguage);

			this._entryOnPointerTap(entry);
		}

		this._languagePage.visible = false;
		this._settingsPage.visible = true;
	}

	destroy() {
		P3.input.signals.keyUp.detach(this._keyUpSignal);
		super.destroy();
	}

	update() {
		super.update();

		if (this._scrollUpHeld) {
			this._langScrollView.content.y += 5;
		} else if (this._scrollDownHeld) {
			this._langScrollView.content.y -= 5;
		}

		this._langScrollView.update(window.dt);
		this.updateScrollThumb();
	}

	appear() {
		super.appear();

		this._fade.alpha = 0;

		new TWEEN.Tween(this._fade, this.tweens)
			.to({
				alpha: 0.5,
			}, 300)
			.easing(TWEEN.Easing.Quadratic.Out)
			.start();
	}

	_onHomeClick() {
		this._panel.visible = false;

		this._warningDialog = new WarningDialog(`icon_${common.selectedLevel}`);

		this._warningDialog.body = i18next.t('gui.quitdialog.body');
		this._warningDialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this.addChild(this._warningDialog);

		this._warningDialog.signals.confirm.add(() => {
			common.paused = false;
			this.signals.home.dispatch();
			common.tracking.track(new P3.TrackingDataGtagEvent('home_click', {
				event_category: 'UI',
				event_type: 'home_click',
				page_container: this._source || 'game',
			}));
		});

		this._warningDialog.signals.deny.add(() => {
			this._panel.visible = true;
		});
	}

	_onRestartClick() {
		this._panel.visible = false;

		this._warningDialog = new WarningDialog(`icon_${common.selectedLevel}`);

		this._warningDialog.body = i18next.t('gui.settings.restartConfirm');

		this._warningDialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		this.addChild(this._warningDialog);

		this._warningDialog.signals.confirm.add(() => {
			common.paused = false;
			this.signals.restart.dispatch();
		});
		this._warningDialog.signals.deny.add(() => {
			this._panel.visible = true;
		});
	}

	_onNextClick() {
		common.paused = false;
		this.signals.next.dispatch();
	}

	resize() {
		super.resize();
		this._panel.position.set(common.STAGE_WIDTH * 0.5 - this._panel.width * 0.5, common.STAGE_HEIGHT * 0.5 - this._panel.height * 0.5);

		if (this._warningDialog) {
			this._warningDialog.position.set(common.STAGE_WIDTH * 0.5, common.STAGE_HEIGHT * 0.5);
		}

		this._fade.width = P3.View.width;
		this._fade.height = P3.View.height;
		this._fade.x = (common.STAGE_WIDTH - P3.View.width) * 0.5;
	}
}
